import React, { useMemo, useState } from 'react';
import { AlertTriangle, Check, Mail, Plus, Star, UserMinus } from 'lucide-react';

import PageCard from '../../PageCard/PageCard';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { cn } from '../../../lib/utils';
import { MemberRole, TeamMember } from '../../../model/team.model';
import {
  categories,
  categoryGroups,
  grantedLeaves,
  memberRoles as initialAssignments,
  members as initialMembers,
  roleGrants,
  roles,
} from './teamMock';

const defaultRole = roles.find(role => role.isDefault);

const PeoplePage: React.FC = () => {
  const [people, setPeople] = useState<TeamMember[]>(initialMembers);
  const [assignments, setAssignments] = useState<MemberRole[]>(initialAssignments);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoles, setInviteRoles] = useState<string[]>(defaultRole ? [defaultRole.id] : []);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [rolesOpenFor, setRolesOpenFor] = useState<string | null>(null);

  const rolesOf = (memberId: string) =>
    assignments
      .filter(link => link.memberId === memberId)
      .map(link => roles.find(role => role.id === link.roleId))
      .filter((role): role is NonNullable<typeof role> => Boolean(role));

  /** How many categories the person actually ends up with, through all their roles. */
  const categoryCount = (memberId: string) =>
    grantedLeaves(
      rolesOf(memberId).map(role => role.id),
      roleGrants,
      categories,
      categoryGroups,
    ).size;

  const toggleRole = (memberId: string, roleId: string) => {
    if (defaultRole && roleId === defaultRole.id) return; // everyone keeps the default
    setAssignments(prev =>
      prev.some(link => link.memberId === memberId && link.roleId === roleId)
        ? prev.filter(link => !(link.memberId === memberId && link.roleId === roleId))
        : [...prev, { memberId, roleId }],
    );
  };

  const invite = () => {
    const email = inviteEmail.trim();
    if (!email.includes('@')) return;
    const id = `invited-${people.length}`;
    const name = email
      .split('@')[0]
      .split(/[._-]/)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

    setPeople(prev => [...prev, { id, name, email, team: '—', status: 'invited' }]);
    setAssignments(prev => [...prev, ...inviteRoles.map(roleId => ({ memberId: id, roleId }))]);
    setInviteEmail('');
    setInviteRoles(defaultRole ? [defaultRole.id] : []);
    setInviteOpen(false);
  };

  /** Removing someone frees the seat; what they logged stays in the company's history. */
  const removePerson = (memberId: string) => {
    setPeople(prev => prev.filter(person => person.id !== memberId));
    setAssignments(prev => prev.filter(link => link.memberId !== memberId));
  };

  const counts = useMemo(
    () => ({
      active: people.filter(person => person.status === 'active').length,
      invited: people.filter(person => person.status === 'invited').length,
    }),
    [people],
  );

  const title = (
    <div>
      <div className="text-base font-medium leading-tight">People</div>
      <div className="text-xs text-muted-foreground">
        {counts.active} active · {counts.invited} invited · {people.length} of 100 seats
      </div>
    </div>
  );

  const extra = (
    <Popover open={inviteOpen} onOpenChange={setInviteOpen}>
      <PopoverTrigger asChild>
        <Button variant="primary" size="sm" className="dark:text-primary-foreground">
          <Plus />
          Invite people
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-3 p-3">
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground">Work email</label>
          <Input
            autoFocus
            value={inviteEmail}
            onChange={event => setInviteEmail(event.target.value)}
            onKeyDown={event => event.key === 'Enter' && invite()}
            placeholder="sam@northwind.co"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground">Roles</label>
          <div className="space-y-0.5">
            {roles.map(role => {
              const checked = inviteRoles.includes(role.id);
              const locked = Boolean(defaultRole && role.id === defaultRole.id);
              return (
                <button
                  key={role.id}
                  type="button"
                  disabled={locked}
                  onClick={() =>
                    setInviteRoles(prev =>
                      prev.includes(role.id)
                        ? prev.filter(item => item !== role.id)
                        : [...prev, role.id],
                    )
                  }
                  className={cn(
                    'flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-sm transition-colors',
                    locked ? 'cursor-default' : 'hover:bg-muted',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
                      checked ? 'border-foreground bg-foreground' : 'border-border',
                    )}
                  >
                    {checked && <Check className="h-3 w-3 text-background" />}
                  </span>
                  <span className={cn(locked && 'text-muted-foreground')}>{role.name}</span>
                  {locked && <Star className="h-2.5 w-2.5 text-amber-600 dark:text-amber-500" />}
                </button>
              );
            })}
          </div>
        </div>

        <p className="text-[11px] leading-snug text-muted-foreground">
          They keep their own account and personal history — joining only adds your categories.
        </p>

        <Button
          variant="primary"
          size="sm"
          className="w-full dark:text-primary-foreground"
          disabled={!inviteEmail.includes('@')}
          onClick={invite}
        >
          <Mail />
          Send invite
        </Button>
      </PopoverContent>
    </Popover>
  );

  return (
    <PageCard title={title} extra={extra}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-separate border-spacing-0">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-muted-foreground">
              <th className="border-b border-border px-2 py-2 text-left font-normal">Person</th>
              <th className="w-32 border-b border-border px-2 py-2 text-left font-normal">Team</th>
              <th className="border-b border-border px-2 py-2 text-left font-normal">Roles</th>
              <th className="w-28 border-b border-border px-2 py-2 text-right font-normal">
                Categories
              </th>
              <th className="w-28 border-b border-border px-2 py-2 text-left font-normal">Status</th>
              <th className="w-10 border-b border-border px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {people.map(person => {
              const personRoles = rolesOf(person.id);
              const count = categoryCount(person.id);

              return (
                <tr key={person.id} className="group">
                  <td className="border-b border-border/40 px-2 py-2">
                    <div className="text-sm font-medium">{person.name}</div>
                    <div className="text-xs text-muted-foreground">{person.email}</div>
                  </td>
                  <td className="border-b border-border/40 px-2 py-2 text-sm text-muted-foreground">
                    {person.team}
                  </td>
                  <td className="border-b border-border/40 px-2 py-2">
                    <div className="flex flex-wrap items-center gap-1">
                      {personRoles.map(role => (
                        <span
                          key={role.id}
                          className={cn(
                            'flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
                            role.isDefault
                              ? 'border-border bg-muted text-muted-foreground'
                              : 'border-border',
                          )}
                        >
                          {role.isDefault && (
                            <Star className="h-2.5 w-2.5 text-amber-600 dark:text-amber-500" />
                          )}
                          {role.name}
                        </span>
                      ))}

                      <Popover
                        open={rolesOpenFor === person.id}
                        onOpenChange={open => setRolesOpenFor(open ? person.id : null)}
                      >
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            title="Change roles"
                            className="flex h-5 w-5 items-center justify-center rounded-full border border-dashed border-border text-muted-foreground transition-colors hover:border-foreground hover:text-foreground"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-56 p-1">
                          {roles.map(role => {
                            const checked = personRoles.some(item => item.id === role.id);
                            const locked = Boolean(defaultRole && role.id === defaultRole.id);
                            return (
                              <button
                                key={role.id}
                                type="button"
                                disabled={locked}
                                onClick={() => toggleRole(person.id, role.id)}
                                className={cn(
                                  'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors',
                                  locked ? 'cursor-default' : 'hover:bg-muted',
                                )}
                              >
                                <span
                                  className={cn(
                                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
                                    checked ? 'border-foreground bg-foreground' : 'border-border',
                                  )}
                                >
                                  {checked && <Check className="h-3 w-3 text-background" />}
                                </span>
                                <span className={cn(locked && 'text-muted-foreground')}>
                                  {role.name}
                                </span>
                                {locked && (
                                  <span className="ml-auto text-[10px] uppercase text-muted-foreground">
                                    default
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </PopoverContent>
                      </Popover>
                    </div>
                  </td>
                  <td className="border-b border-border/40 px-2 py-2 text-right">
                    {count > 0 ? (
                      <span className="text-sm tabular-nums">{count}</span>
                    ) : (
                      <span
                        title="No roles grant anything — this person opens an empty app"
                        className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-500"
                      >
                        <AlertTriangle className="h-3 w-3" />0
                      </span>
                    )}
                  </td>
                  <td className="border-b border-border/40 px-2 py-2">
                    {person.status === 'active' ? (
                      <span className="text-xs text-muted-foreground">Active</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 px-2 py-0.5 text-xs text-amber-700 dark:text-amber-400">
                        Invited
                      </span>
                    )}
                  </td>
                  <td className="border-b border-border/40 px-2 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => removePerson(person.id)}
                      title="Remove from the company — frees the seat, logged history stays"
                      className="rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-destructive group-hover:opacity-100"
                    >
                      <UserMinus className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        Removing someone stops your categories reaching their app and frees a seat. What they already
        logged stays in your reports, and their personal tracking was never yours to begin with.
      </p>
    </PageCard>
  );
};

export default PeoplePage;
